import React, { useState, useMemo } from 'react';
import styles from './FundDetailsPreliminaryImportantInfo.module.scss'; // SCSS Module file

import AlertMessageBase from '@/components/Generic/AlertMessageBase';
import ExpandableContent from '@/components/Generic/ExpandableContent';
import EEPlaceholder from '@/components/Sections/EEPlaceholder';
import FundDetailsTabSkeleton from './FundDetailsTabSkeleton';
import classNames from 'classnames';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { useFetchPreliminaryImportantInformationQuery } from '@/store/fundDetails/services/fundDetailsApi';
import usePayload from 'hooks/useFundDetailsRequestPayload';
import { useI18n } from 'next-localization';
import useHelpers from 'hooks/helpers';

const linesToShowByDefault = 2;

const FundDetailsPreliminaryImportantInfo: React.FC = () => {
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const [showMore, setShowMore] = useState(false);
  const { getErrorDictionary } = useHelpers();
  const { shareClass, correlationFundRangeId, countryCode, language } = usePayload();
  const { pageEditing } = sitecoreContext;
  const { data, isFetching, error } = useFetchPreliminaryImportantInformationQuery({
    countryCode,
    language,
    shareClass: shareClass,
    correlationFundRangeId: correlationFundRangeId ?? '',
  });

  const lineItems = useMemo(() => {
    return {
      shown: data?.content?.preliminaryInfo?.slice(0, linesToShowByDefault) ?? [],
      hidden: data?.content?.preliminaryInfo?.slice(linesToShowByDefault) ?? [],
    };
  }, [data]);

  if (pageEditing) {
    return <EEPlaceholder>Fund Details Preliminary Important Info</EEPlaceholder>;
  }

  return (
    <div className="px-2 px-md-3 px-lg-5 py-4 background-digital-light-grey">
      <h2 className="h5">{t('fundDetailsPreliminaryImportantInformation')}</h2>

      <div className="mt-3">
        {isFetching && <FundDetailsTabSkeleton />}

        {!isFetching && error && (
          <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />
        )}

        {!isFetching && !error && lineItems.shown.length > 0 && (
          <ul className={styles.list}>
            {lineItems.shown.map((line, index) => (
              <li key={line} className={`body-small ${index !== 0 ? 'mt-2' : ''}`}>
                {line}
              </li>
            ))}

            {lineItems.hidden.length > 0 && (
              <>
                <ExpandableContent show={showMore}>
                  <div className="pt-2">
                    {lineItems.hidden.map((line, index) => (
                      <li key={line} className={`body-small ${index !== 0 ? 'mt-2' : ''}`}>
                        {line}
                      </li>
                    ))}
                  </div>
                </ExpandableContent>

                <button
                  className="mt-3 btn-naked d-flex gap-1"
                  aria-expanded={showMore ? 'true' : 'false'}
                  onClick={() => setShowMore(!showMore)}
                >
                  <span className="text-link">{showMore ? t('showLess') : t('showMore')}</span>
                  <i
                    aria-hidden
                    className={classNames('bi bi-chevron-down', styles.icon, styles.iconChevron, {
                      [styles.iconChevronExpanded]: showMore,
                    })}
                  ></i>
                </button>
              </>
            )}
          </ul>
        )}

        {!isFetching && !error && lineItems.shown.length === 0 && (
          <div className="body-default">{t('noResultsMessage')}</div>
        )}
      </div>
    </div>
  );
};

export default FundDetailsPreliminaryImportantInfo;
